import { createSlice } from '@reduxjs/toolkit';

// Load activities from localStorage
const loadActivitiesFromLocalStorage = () => {
  try {
    const savedActivities = localStorage.getItem('userActivities');
    return savedActivities ? JSON.parse(savedActivities) : [];
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('Failed to load activities from localStorage:', err);
    return [];
  }
};

const initialState = {
  activities: loadActivitiesFromLocalStorage(),
  loading: false,
  error: null,
  lastFetched: null,
};

const activitiesSlice = createSlice({
  name: 'activities',
  initialState,
  reducers: {
    setActivities: (state, action) => {
      state.activities = action.payload;
      state.lastFetched = Date.now();
      state.loading = false;
      state.error = null;
      // Persist to localStorage
      localStorage.setItem('userActivities', JSON.stringify(action.payload));
    },

    addActivity: (state, action) => {
      state.activities.push(action.payload);
      localStorage.setItem('userActivities', JSON.stringify(state.activities));
    },

    updateActivity: (state, action) => {
      const { id, updates } = action.payload;
      const index = state.activities.findIndex((activity) => activity.id === id);
      if (index !== -1) {
        state.activities[index] = { ...state.activities[index], ...updates };
        localStorage.setItem('userActivities', JSON.stringify(state.activities));
      }
    },

    removeActivity: (state, action) => {
      const id = action.payload;
      state.activities = state.activities.filter((activity) => activity.id !== id);
      localStorage.setItem('userActivities', JSON.stringify(state.activities));
    },

    toggleActivityComplete: (state, action) => {
      const id = action.payload;
      const activity = state.activities.find((a) => a.id === id);
      if (activity) {
        activity.completed = !activity.completed;
        activity.completedAt = activity.completed ? new Date().toISOString() : null;
        localStorage.setItem('userActivities', JSON.stringify(state.activities));
      }
    },

    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },

    clearActivities: (state) => {
      state.activities = [];
      state.lastFetched = null;
      localStorage.removeItem('userActivities');
    },
  },
});

export const {
  setActivities,
  addActivity,
  updateActivity,
  removeActivity,
  toggleActivityComplete,
  setLoading,
  setError,
  clearActivities,
} = activitiesSlice.actions;

export default activitiesSlice.reducer;

// Selectors
export const selectAllActivities = (state) => state.activities.activities;
export const selectActiveActivities = (state) =>
  state.activities.activities.filter((a) => !a.completed);
export const selectCompletedActivities = (state) =>
  state.activities.activities.filter((a) => a.completed);
export const selectActivitiesLoading = (state) => state.activities.loading;
export const selectActivitiesError = (state) => state.activities.error;
export const selectLastFetched = (state) => state.activities.lastFetched;

// Advanced selectors for task organization
export const selectActivitiesByPriority = (state) => {
  const activities = state.activities.activities;
  return {
    urgent: activities.filter((a) => a.priority === 'urgent'),
    high: activities.filter((a) => a.priority === 'high'),
    medium: activities.filter((a) => a.priority === 'medium'),
    low: activities.filter((a) => a.priority === 'low'),
  };
};

export const selectActivitiesByDate = (state) => {
  const activities = state.activities.activities;
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - today.getDay());
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 7);

  return {
    overdue: activities.filter((a) => {
      if (!a.when || a.completed) return false;
      return new Date(a.when) < todayStart;
    }),
    today: activities.filter((a) => {
      if (!a.when) return false;
      const taskDate = new Date(a.when);
      return (
        taskDate.getDate() === today.getDate() &&
        taskDate.getMonth() === today.getMonth() &&
        taskDate.getFullYear() === today.getFullYear()
      );
    }),
    thisWeek: activities.filter((a) => {
      if (!a.when) return false;
      const taskDate = new Date(a.when);
      return taskDate >= weekStart && taskDate < weekEnd;
    }),
    later: activities.filter((a) => {
      if (!a.when) return true;
      const taskDate = new Date(a.when);
      return taskDate >= weekEnd;
    }),
  };
};

export const selectActivitiesByStatus = (state) => {
  const activities = state.activities.activities;
  return {
    todo: activities.filter((a) => a.status === 'todo'),
    inProgress: activities.filter((a) => a.status === 'in_progress'),
    done: activities.filter((a) => a.status === 'done' || a.completed),
  };
};
